/**
 * "Abrir admissão no IW", dentro do bloco do vínculo (NR-256), no Caso já vinculado ao paciente do IW.
 *
 * Abrir a admissão é saída externa: sai só com a confirmação de uma pessoa (restrição 9). O que já aconteceu vem dos
 * envios ao IW do Caso — admissão aberta (com o id) ou na fila aparece no lugar do botão, e o botão só volta se a
 * última tentativa foi recusada ou esgotou. A confirmação pede o grupo de entrega e o de dispensação (NR-262) e a data
 * de internação (NR-261), obrigatória e dentro da faixa do back, que a pessoa informa e confere: a plataforma não sugere
 * nenhuma.
 *
 * O estado do formulário é de UM Caso: o `DetalheDoCaso` monta o `VinculoComOIw` com `key={caso.id}`, e trocar de
 * `/casos/:id` remonta este componente com a data vazia.
 */

import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroDaApi } from '../../shared/api/erros';
import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import {
  abrirAdmissaoNoIw,
  faixaDaDataDeInternacao,
  formatarDiaDaInternacao,
  opcoesDosGruposDaAdmissao,
  rotuloDoGrupo,
  type GruposEscolhidos,
  type ResultadoDaAdmissaoNoIw,
} from './api/admissaoNoIw';
import { ROTULOS_DO_CAMPO_DO_IW } from './api/cadastroNoIw';
import { chavesDeCasos } from './api/casos';
import { opcoesDosEnviosAoIw } from './api/enviosAoIw';
import type { CasoDetalhado } from './api/tipos';
import { opcoesDasAdmissoesDoPacienteNoIw, type AdmissaoDoPacienteNoIw } from './api/vinculoIw';

/** A filial do grupo e a do cadastro, sem caixa, acento, espaço nem pontuação: o IW e a configuração não escrevem igual. */
function mesmaFilial(a: string, b: string): boolean {
  const chave = (texto: string): string =>
    texto
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/[^\p{L}\p{N}]/gu, '')
      .toLowerCase();
  return chave(a) === chave(b);
}

/**
 * O que o paciente já tem no IW, dito antes do clique, na mesma ordem em que a fila decide: status desconhecido recusa
 * a abertura (o botão sai), em avaliação é adotada, e em atendimento só avisa, porque abrir outra é escolha de quem clica.
 */
function avisoDaAdmissaoExistente(
  admissoes: readonly AdmissaoDoPacienteNoIw[],
): { texto: string; bloqueia: boolean } | null {
  const desconhecida = admissoes.find((a) => a.status === 'Desconhecido');
  if (desconhecida) {
    return {
      texto: `Este paciente tem no IW uma admissão com status que a plataforma não conhece (id ${desconhecida.id}, código ${desconhecida.statusCru}). Confira no IW.`,
      bloqueia: true,
    };
  }
  const emAvaliacao = admissoes.find((a) => a.status === 'InEvaluation');
  if (emAvaliacao) {
    return {
      texto: `Este paciente já tem admissão em avaliação no IW (id ${emAvaliacao.id}). Abrir aqui adota essa, sem criar outra.`,
      bloqueia: false,
    };
  }
  const emAtendimento = admissoes.find((a) => a.status === 'InCare');
  if (emAtendimento) {
    return {
      texto: `Este paciente já tem admissão em atendimento no IW (id ${emAtendimento.id}). Se abrir aqui, ele fica com outra admissão no IW.`,
      bloqueia: false,
    };
  }
  return null;
}

function mensagemDeErroDaAdmissao(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    switch (causa.codigo) {
      case 'IW_ADMISSION_DISABLED':
        return 'A abertura de admissão no IW ainda não está ligada neste ambiente. Abra a admissão direto no IW.';
      case 'IW_ADMISSION_REQUIRES_LINK':
        return 'Vincule o Caso ao paciente do IW antes de abrir a admissão.';
      case 'IW_ADMISSION_ALREADY_OPEN':
        return 'A admissão deste Caso já foi aberta no IW.';
      case 'IW_ADMISSION_INCOMPLETE':
      case 'IW_ADMISSION_INVALID_GROUP':
      case 'IW_ADMISSION_DATE_REQUIRED':
      case 'IW_ADMISSION_DATE_OUT_OF_RANGE':
      case 'IW_ADMISSION_ALREADY_PENDING':
        return causa.message;
    }
    if (causa.status === 404) return 'O caso não foi encontrado para o seu núcleo.';
  }
  return 'Não foi possível abrir a admissão no IW. Tente novamente.';
}

function mensagemDeErroDosGrupos(causa: unknown): string {
  // Falha que não melhora com o tempo (rota ausente, credencial recusada): o catálogo diz o que fazer.
  if (
    causa instanceof ErroDaApi &&
    (causa.codigo === 'IW_ADMISSION_GROUPS_UNAVAILABLE' || causa.codigo === 'IW_CREDENTIAL_REJECTED')
  ) {
    return `Não foi possível ler os grupos do IW. ${causa.message}`;
  }
  return 'Não foi possível ler os grupos do IW. Tente de novo em instantes; sem eles a admissão não é aberta.';
}

function mensagemDoDesfecho(resultado: ResultadoDaAdmissaoNoIw): string {
  switch (resultado.desfecho) {
    case 'Opened':
      return resultado.idAdmissao !== null
        ? `Admissão ${resultado.idAdmissao} aberta no IW por este Caso.`
        : 'Admissão aberta no IW por este Caso.';
    case 'Pending':
      return 'O IW não abriu agora (o registro pode estar travado lá, ou o IW não respondeu). A abertura ficou na fila e é refeita sozinha, sem duplicar.';
    case 'Refused': {
      const campos = resultado.camposRecusados
        .map((c) => `${ROTULOS_DO_CAMPO_DO_IW[c.campo] ?? c.campo}: ${c.mensagem}`)
        .join('; ');
      const detalhe = [campos, resultado.motivo].filter((parte) => parte !== null && parte !== '').join(' ');
      // A recusa da plataforma não é do IW: atribuí-la ao IW manda a pessoa procurar o problema no lugar errado.
      const quem = resultado.recusadoPelaPlataforma ? 'A admissão não foi aberta.' : 'O IW recusou a admissão.';
      return detalhe === '' ? quem : `${quem} ${detalhe}`;
    }
    case 'Unanswered':
      return 'O IW não respondeu em nenhuma tentativa, e nada foi confirmado. Tente de novo quando ele voltar.';
    default:
      return 'O IW respondeu de um jeito que esta tela ainda não conhece. Recarregue o Caso para ver o estado atual.';
  }
}

export function AdmissaoNoIw({
  caso,
  aoAtualizar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
}): React.JSX.Element | null {
  const clienteDeConsultas = useQueryClient();
  const [confirmando, definirConfirmando] = useState(false);
  const envios = useQuery(opcoesDosEnviosAoIw(caso.id));
  const grupos = useQuery(opcoesDosGruposDaAdmissao());
  // A escolha é da pessoa (NR-262): o grupo de entrega é a rota por região, e o IW aceita qualquer código sem avisar.
  const [dispensacao, definirDispensacao] = useState<number | null>(null);
  const [entrega, definirEntrega] = useState<number | null>(null);
  const [dataInternacao, definirDataInternacao] = useState('');
  const faixa = faixaDaDataDeInternacao();
  // `yyyy-MM-dd` compara como texto; o back confere de novo, e o `min`/`max` do campo não segura o que é digitado.
  const dataNaFaixa = dataInternacao !== '' && dataInternacao >= faixa.minima && dataInternacao <= faixa.maxima;

  const abrir = useMutation({
    mutationFn: (escolha: GruposEscolhidos) => abrirAdmissaoNoIw(caso.id, escolha),
    onSuccess: (resultado) => {
      aoAtualizar(resultado.caso);
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.enviosAoIw(caso.id) });
      void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.linhaDoTempo(caso.id) });
    },
    onError: (causa) => {
      // Já na fila com os dados do primeiro clique: recarrega os envios para a tela mostrar a abertura em andamento.
      if (causa instanceof ErroDaApi && causa.codigo === 'IW_ADMISSION_ALREADY_PENDING') {
        void clienteDeConsultas.invalidateQueries({ queryKey: chavesDeCasos.enviosAoIw(caso.id) });
      }
    },
  });

  // A última abertura manda: aberta ou na fila tira o botão; recusada ou esgotada o devolve.
  const ultima = envios.data?.find((e) => e.tipo === 'OpenAdmission') ?? null;
  const estado =
    ultima?.estado === 'Completed'
      ? ultima.idNoIw !== null
        ? `Admissão ${ultima.idNoIw} aberta no IW por este Caso.`
        : 'Admissão aberta no IW por este Caso.'
      : ultima?.estado === 'Pending'
        ? 'Abertura da admissão na fila: o IW ainda não respondeu, e ela é refeita sozinha.'
        : null;

  const admissoes = useQuery(opcoesDasAdmissoesDoPacienteNoIw(caso.id, estado === null));
  const aviso = admissoes.data ? avisoDaAdmissaoExistente(admissoes.data.admissoes) : null;
  // NR-256 + cadastro: o grupo de dispensação é de uma filial. Com a filial do paciente conhecida (foi este Caso que o
  // cadastrou), só os grupos dela aparecem, e o grupo padrão de outra filial não vem marcado. Sem ela (vínculo pela
  // busca: o IW não devolve a filial), ou com a filial escrita de outro jeito sem grupo nenhum, a lista vem inteira.
  const filialDoPaciente = admissoes.data?.filialDoPaciente ?? null;
  const todosOsGrupos = grupos.data?.dispensacao ?? [];
  const daFilial =
    filialDoPaciente === null
      ? []
      : todosOsGrupos.filter((g) => g.filial !== null && mesmaFilial(g.filial, filialDoPaciente));
  const gruposDeDispensacao = daFilial.length > 0 ? daFilial : todosOsGrupos;
  const padraoDaFilial = gruposDeDispensacao.some((g) => g.codigo === grupos.data?.padraoDispensacao)
    ? (grupos.data?.padraoDispensacao ?? null)
    : null;
  const dispensacaoEscolhida = dispensacao ?? padraoDaFilial;
  // A rota não tem filial no IW, mas o nome começa pela UF ("SP - Oeste - Quarta"), a mesma do fim do nome da filial
  // ("ANERY SP"). Com a UF conhecida, só as rotas dela; se nenhuma casar, a lista vem inteira.
  // Sem RegExp: o nome vem da configuração, e "ANERY (RJ)" ou um "(" no fim quebrariam o padrão.
  const ufDaFilial = filialDoPaciente?.trim().split(/\s+/).at(-1)?.toUpperCase() ?? null;
  const rotasDaUf =
    ufDaFilial === null
      ? []
      : (grupos.data?.entrega ?? []).filter((g) => {
          const [prefixo] = g.nome.split('-', 1);
          return g.nome.includes('-') && prefixo?.trim().toUpperCase() === ufDaFilial;
        });
  const rotas = ufDaFilial !== null && rotasDaUf.length > 0 ? rotasDaUf : (grupos.data?.entrega ?? []);
  const padraoDaRota = rotas.some((g) => g.codigo === grupos.data?.padraoEntrega)
    ? (grupos.data?.padraoEntrega ?? null)
    : null;
  const entregaEscolhida = entrega ?? padraoDaRota;

  return (
    <div className="space-y-2">
      {estado === null && aviso !== null ? (
        <p role="status" className="text-[0.8125rem] font-semibold">
          {aviso.texto}
        </p>
      ) : null}
      {estado === null && admissoes.isError ? (
        <p role="status" className="text-[0.8125rem]">
          Não foi possível ler agora as admissões deste paciente no IW. Confira no IW se já há uma em atendimento antes
          de abrir outra.
        </p>
      ) : null}
      {estado !== null ? (
        <p role="status" className="text-[0.8125rem]">
          {estado}
        </p>
      ) : aviso?.bloqueia ? null : (
        <>
          <Button
            variant="outlined"
            disabled={abrir.isPending}
            onClick={() => {
              abrir.reset();
              definirConfirmando(true);
            }}
          >
            Abrir admissão no IW
          </Button>
          {abrir.isSuccess ? (
            <p role="status" className="text-[0.8125rem]">
              {mensagemDoDesfecho(abrir.data)}
            </p>
          ) : null}
        </>
      )}

      {abrir.isError ? (
        <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
          {mensagemDeErroDaAdmissao(abrir.error)}
        </p>
      ) : null}

      <ConfirmacaoHumana
        aberto={confirmando}
        titulo="Abrir a admissão no IW?"
        descricao="A plataforma abre a admissão deste paciente no IW, com os grupos e a data de internação informados abaixo. Se o IW estiver ocupado com o registro, a abertura fica na fila e é refeita sozinha, sem criar duas."
        rotuloDeConfirmar="Abrir admissão"
        confirmarDesabilitado={
          grupos.isError || dispensacaoEscolhida === null || entregaEscolhida === null || !dataNaFaixa
        }
        aoCancelar={() => {
          definirConfirmando(false);
        }}
        aoConfirmar={() => {
          if (dispensacaoEscolhida === null || entregaEscolhida === null || !dataNaFaixa) return;
          definirConfirmando(false);
          abrir.mutate({ dispensacao: dispensacaoEscolhida, entrega: entregaEscolhida, dataInternacao });
        }}
      >
        {grupos.isError ? (
          <div className="flex flex-col items-start gap-1">
            <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
              {mensagemDeErroDosGrupos(grupos.error)}
            </p>
            <Button
              size="small"
              variant="outlined"
              disabled={grupos.isFetching}
              onClick={() => {
                void grupos.refetch();
              }}
            >
              Tentar de novo
            </Button>
          </div>
        ) : null}
        <TextField
          select
          size="small"
          label="Grupo de entrega (rota)"
          value={entregaEscolhida ?? ''}
          helperText={
            rotas === rotasDaUf && ufDaFilial !== null
              ? `Só as rotas de ${ufDaFilial}, a UF da filial do paciente.`
              : undefined
          }
          onChange={(evento) => {
            definirEntrega(Number(evento.target.value));
          }}
        >
          {rotas.map((g) => (
            <MenuItem key={g.codigo} value={g.codigo}>
              {rotuloDoGrupo(g)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Grupo de dispensação"
          value={dispensacaoEscolhida ?? ''}
          onChange={(evento) => {
            definirDispensacao(Number(evento.target.value));
          }}
          helperText={
            daFilial.length > 0 && filialDoPaciente !== null
              ? `Só os grupos da filial do paciente (${filialDoPaciente}).`
              : undefined
          }
        >
          {gruposDeDispensacao.map((g) => (
            <MenuItem key={g.codigo} value={g.codigo}>
              {rotuloDoGrupo(g)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          type="date"
          size="small"
          required
          label="Data de internação"
          value={dataInternacao}
          onChange={(evento) => {
            definirDataInternacao(evento.target.value);
          }}
          error={dataInternacao !== '' && !dataNaFaixa}
          helperText={`O dia em que o home care começa, não o do hospital de origem, entre ${formatarDiaDaInternacao(faixa.minima)} e ${formatarDiaDaInternacao(faixa.maxima)}. Obrigatória: a plataforma não corrige a admissão depois de aberta.`}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: faixa.minima, max: faixa.maxima } }}
        />
      </ConfirmacaoHumana>
    </div>
  );
}
